import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import type { Tier, TierId, BillingCycle } from './tiersData';

interface PricingCardProps {
  tier: Tier;
  billingCycle: BillingCycle;
  /** If provided, renders tier-hierarchy CTAs (current badge / upgrade CTA / downgrade link). */
  currentTier?: TierId;
  /** Custom handler for the tier CTA. Overrides default navigate-to-register behavior. */
  onSelect?: (tier: TierId) => void;
}

const TIER_RANK: Record<TierId, number> = { free: 0, pro: 1, business: 2 };

const formatPrice = (cents: number): string => {
  if (cents === 0) return '0';
  return cents.toLocaleString('pt-BR');
};

export const PricingCard: React.FC<PricingCardProps> = ({ tier, billingCycle, currentTier, onSelect }) => {
  const navigate = useNavigate();
  const Icon = tier.icon;
  const isAnnual = billingCycle === 'annual';
  const price = isAnnual ? tier.priceAnnual : tier.priceMonthly;
  const priceUnit = isAnnual ? '/ano' : '/mês';
  const showSavings = isAnnual && tier.annualSavings > 0;

  const isCurrent = currentTier === tier.id;
  const isUpgrade = currentTier !== undefined && TIER_RANK[tier.id] > TIER_RANK[currentTier];
  const isDowngrade = currentTier !== undefined && TIER_RANK[tier.id] < TIER_RANK[currentTier];

  const handleDefaultCta = () => {
    if (tier.ctaIntent === 'register') {
      navigate('/auth/register');
    } else {
      navigate(`/auth/register?intent=upgrade&tier=${tier.id}&cycle=${billingCycle}`);
    }
  };

  const handleCta = () => {
    if (onSelect) {
      onSelect(tier.id);
    } else {
      handleDefaultCta();
    }
  };

  const borderClass = tier.highlight && !isCurrent
    ? 'border-primary-500/60 bg-gradient-to-b from-primary-500/10 to-neutral-900/80'
    : isCurrent
      ? 'border-emerald-500/40 bg-neutral-900/60'
      : 'border-neutral-700/50 bg-neutral-900/60';

  const upgradeCtaClass = tier.highlight
    ? 'bg-primary-600 hover:bg-primary-700 text-white'
    : 'bg-primary-600 hover:bg-primary-700 text-white';

  return (
    <article
      className={`relative flex flex-col rounded-2xl border ${borderClass} backdrop-blur-xl p-6 sm:p-8 transition-transform ${
        tier.highlight && !isCurrent ? 'lg:scale-[1.02]' : ''
      }`}
      aria-labelledby={`tier-${tier.id}-name`}
    >
      {tier.badge && !isCurrent && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold bg-primary-700 text-white shadow-lg shadow-primary-500/30">
          {tier.badge}
        </span>
      )}
      {isCurrent && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-600 text-white shadow-lg shadow-emerald-500/30">
          Plano atual
        </span>
      )}

      <div className="flex items-center gap-3 mb-2">
        <span
          className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            tier.highlight && !isCurrent ? 'bg-primary-500/20 text-primary-400' : 'bg-neutral-800 text-neutral-400'
          }`}
        >
          <Icon className="w-5 h-5" />
        </span>
        <h2 id={`tier-${tier.id}-name`} className="text-xl font-semibold text-white">
          {tier.name}
        </h2>
      </div>

      <p className="text-sm text-neutral-400 mb-6">{tier.tagline}</p>

      <div className="mb-6">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-semibold text-neutral-400">R$</span>
          <span className="text-4xl sm:text-5xl font-bold text-white tracking-tight">
            {formatPrice(price)}
          </span>
          <span className="text-sm text-neutral-400">{priceUnit}</span>
        </div>
        {showSavings && (
          <p className="mt-2 text-sm text-emerald-400 font-medium">
            Economize R$ {tier.annualSavings}/ano
          </p>
        )}
        {!showSavings && tier.priceMonthly > 0 && isAnnual === false && (
          <p className="mt-2 text-xs text-neutral-300">
            ou R$ {formatPrice(tier.priceAnnual)}/ano (20% off)
          </p>
        )}
      </div>

      {isCurrent ? (
        <div
          className="min-h-[44px] w-full px-4 py-3 rounded-xl font-medium text-sm text-center mb-6 bg-neutral-800/40 text-neutral-400 border border-neutral-800"
          aria-label="Você está neste plano"
        >
          Você está aqui
        </div>
      ) : isDowngrade ? (
        <button
          type="button"
          onClick={handleCta}
          className="min-h-[44px] w-full px-4 py-3 rounded-xl font-medium text-sm transition-colors mb-6 bg-transparent text-neutral-400 hover:text-neutral-200 underline underline-offset-4"
        >
          Fazer downgrade para {tier.name}
        </button>
      ) : (
        <button
          type="button"
          onClick={handleCta}
          className={`min-h-[44px] w-full px-4 py-3 rounded-xl font-semibold text-sm transition-colors mb-6 shadow-lg shadow-primary-500/20 ${upgradeCtaClass}`}
        >
          {isUpgrade ? `Fazer upgrade para ${tier.name}` : tier.ctaLabel}
        </button>
      )}

      <ul className="space-y-3 flex-1">
        {tier.summaryFeatures.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-sm text-neutral-300">
            <Check
              className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                tier.highlight && !isCurrent ? 'text-primary-400' : 'text-emerald-400'
              }`}
            />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </article>
  );
};
